/* eslint-disable max-lines */
import { useMemo } from 'react';
import { useLocation, useParams as useReactRouterParams } from 'react-router';

import { replaceParams } from '@dreryk/drerykweb-main-frontend';

// TODO: DREGAB-5175 https://dreryk.atlassian.net/browse/DREGAB-5175 Move the code to a new repository and make better clean code
type Primitives = string | number;
type ParamsData = Partial<Record<string, Primitives>>;
type SerializedParams<PARAMS extends ParamsData> = Record<keyof PARAMS, string>;
type MappedSearchParams<SEARCH_PARAMS extends ParamsData> = Partial<Record<keyof SEARCH_PARAMS, Primitives>>;
type LocationData = {
   params?: ParamsData;
   searchParams?: ParamsData;
};
export type GetParamaData<PATH_FUNCTION> = PATH_FUNCTION extends (data: infer LOCATION_DATA) => string
   ? LOCATION_DATA extends LocationData
      ? LOCATION_DATA
      : never
   : never;
export type GetParams<PATH_FUNCTION> = GetParamaData<PATH_FUNCTION> extends { params: infer PARAMS }
   ? PARAMS extends ParamsData
      ? PARAMS
      : never
   : never;
export type GetSearchParams<PATH_FUNCTION> = GetParamaData<PATH_FUNCTION> extends {
   searchParams: infer SEARCH_PARAMS;
}
   ? SEARCH_PARAMS extends ParamsData
      ? SEARCH_PARAMS
      : never
   : never;
const serializeParams = <PARAMS_DATA extends ParamsData>(params: PARAMS_DATA): SerializedParams<PARAMS_DATA> => {
   return Object.keys(params).reduce((result, key: keyof PARAMS_DATA) => {
      const currentValue = params[key];
      if (currentValue) {
         result[key] = currentValue.toString();
      }
      return result;
   }, {} as SerializedParams<PARAMS_DATA>);
};
const mapSearchParams = <PARAMS_DATA extends ParamsData>(
   paramsData: PARAMS_DATA,
   paramsMapper: Record<keyof PARAMS_DATA, string>
): MappedSearchParams<PARAMS_DATA> => {
   return Object.keys(paramsData).reduce((result, key: keyof PARAMS_DATA) => {
      const mappedKey = paramsMapper[key];
      result[mappedKey] = paramsData[key];
      return result;
   }, {} as MappedSearchParams<PARAMS_DATA>);
};
export const createPath = <LOCATION_DATA extends LocationData | undefined>(
   routePath: string,
   data: LOCATION_DATA,
   searchParamsMapper: LOCATION_DATA extends { searchParams: infer SEARCH_PARAMS }
      ? Record<keyof SEARCH_PARAMS, string>
      : undefined
): string => {
   const pathSegments = [];

   if (!data) {
      return routePath;
   }

   if (data.params) {
      pathSegments.push(replaceParams(routePath, serializeParams(data.params)));
   } else {
      pathSegments.push(routePath);
   }

   if (data.searchParams && searchParamsMapper) {
      const queryString = new URLSearchParams(
         serializeParams(mapSearchParams(data.searchParams, searchParamsMapper))
      ).toString();

      if (queryString) {
         pathSegments.push(queryString);
      }
   }

   return pathSegments.join('?');
};
const parseParamToInteger = (param: string): string | number => {
   return isNaN(Number(param)) ? param : Number.parseInt(param, 10);
};
export const useParams = <PATH_FUNCTION>(): GetParams<PATH_FUNCTION> => {
   const params = useReactRouterParams<GetParams<PATH_FUNCTION>>();
   return Object.keys(params).reduce((result: GetParams<PATH_FUNCTION>, key: string) => {
      const parsedParamValue = parseParamToInteger(params[key]);
      // TODO: DREGAB-5175 Fix my type
      (result as any)[key] = parsedParamValue as any;
      return result;
   }, {} as GetParams<PATH_FUNCTION>);
};
export const useSearchParams = <PATH_FUNCTION>(
   searchParamsMapper: Record<string, keyof GetSearchParams<PATH_FUNCTION>>
): GetSearchParams<PATH_FUNCTION> => {
   const { search } = useLocation();
   return useMemo(() => {
      const searchParams = Object.fromEntries(new URLSearchParams(search).entries());
      return Object.keys(searchParams).reduce((result: GetSearchParams<PATH_FUNCTION>, key: string) => {
         const mappedKey = searchParamsMapper[key];
         const parsedParamValue = parseParamToInteger(searchParams[key]);
         // TODO: DREGAB-5175 Fix my type
         result[mappedKey] = parsedParamValue as any;
         return result;
      }, {} as GetSearchParams<PATH_FUNCTION>);
   }, [searchParamsMapper, search]);
};
